import { assign, setup } from 'xstate5';

import { AUDIENCE_MODES } from '../constants';
import { addCriteriaToDraft, createInitialDraft, definitionToDraft, draftToDefinition } from '../utils';

export const STATES = {
  ALL_VISITORS: 'allVisitors',
  TARGETED: 'targeted',
  EDITING: 'editing',
};

export const ACTIONS = {
  HYDRATE: 'HYDRATE',
  SELECT_ALL: 'SELECT_ALL',
  BEGIN_TARGETING: 'BEGIN_TARGETING',
  EDIT_EXISTING: 'EDIT_EXISTING',
  APPLY_DRAFT: 'APPLY_DRAFT',
  CANCEL_EDIT: 'CANCEL_EDIT',
  SAVE_DRAFT: 'SAVE_DRAFT',
};

function getEditDraftSeed(definition, criteria) {
  const draft = definitionToDraft(definition);

  if (!criteria) {
    return draft;
  }

  return addCriteriaToDraft(draft, criteria);
}

export const audienceBuilderMachine = setup({}).createMachine({
  id: 'audienceBuilder',
  initial: STATES.ALL_VISITORS,
  context: ({ input }) => ({
    definition: input?.definition ?? null,
    draftSeed: null,
    mode: input?.mode ?? AUDIENCE_MODES.TARGETING,
  }),
  states: {
    [STATES.ALL_VISITORS]: {
      always: {
        target: STATES.TARGETED,
        guard: ({ context }) => context.definition !== null,
      },
      on: {
        [ACTIONS.HYDRATE]: {
          actions: assign({
            definition: ({ event }) => event.definition,
            draftSeed: null,
          }),
        },
        [ACTIONS.BEGIN_TARGETING]: {
          target: STATES.EDITING,
          actions: assign({
            draftSeed: ({ event }) => createInitialDraft(event.criteria),
          }),
        },
      },
    },
    [STATES.TARGETED]: {
      always: {
        target: STATES.ALL_VISITORS,
        guard: ({ context }) => context.definition === null,
      },
      on: {
        [ACTIONS.HYDRATE]: {
          actions: assign({
            definition: ({ event }) => event.definition,
            draftSeed: null,
          }),
        },
        [ACTIONS.SELECT_ALL]: {
          target: STATES.ALL_VISITORS,
          actions: assign({
            definition: null,
            draftSeed: null,
          }),
        },
        [ACTIONS.BEGIN_TARGETING]: {
          target: STATES.EDITING,
          actions: assign({
            draftSeed: ({ event }) => createInitialDraft(event.criteria),
          }),
        },
        [ACTIONS.EDIT_EXISTING]: {
          target: STATES.EDITING,
          actions: assign({
            draftSeed: ({ context, event }) => getEditDraftSeed(context.definition, event.criteria),
          }),
        },
      },
    },
    [STATES.EDITING]: {
      on: {
        [ACTIONS.APPLY_DRAFT]: [
          {
            target: STATES.TARGETED,
            guard: ({ event }) => draftToDefinition(event.draft) != null,
            actions: assign({
              definition: ({ event }) => draftToDefinition(event.draft),
              draftSeed: null,
            }),
          },
          {
            target: STATES.ALL_VISITORS,
            actions: assign({
              definition: null,
              draftSeed: null,
            }),
          },
        ],
        [ACTIONS.CANCEL_EDIT]: [
          {
            target: STATES.TARGETED,
            guard: ({ context }) => context.definition != null,
            actions: assign({
              draftSeed: null,
            }),
          },
          {
            target: STATES.ALL_VISITORS,
            actions: assign({
              draftSeed: null,
            }),
          },
        ],
        [ACTIONS.SELECT_ALL]: {
          target: STATES.ALL_VISITORS,
          actions: assign({
            definition: null,
            draftSeed: null,
          }),
        },
        [ACTIONS.BEGIN_TARGETING]: {
          actions: assign({
            draftSeed: ({ event }) => createInitialDraft(event.criteria),
          }),
        },
        [ACTIONS.EDIT_EXISTING]: {
          actions: assign({
            draftSeed: ({ context, event }) => getEditDraftSeed(context.definition, event.criteria),
          }),
        },
        [ACTIONS.SAVE_DRAFT]: {
          actions: assign({
            draftSeed: ({ event }) => event.draft,
          }),
        },
        [ACTIONS.HYDRATE]: [
          {
            target: STATES.TARGETED,
            guard: ({ event }) => event.definition !== null,
            actions: assign({
              definition: ({ event }) => event.definition,
              draftSeed: null,
            }),
          },
          {
            target: STATES.ALL_VISITORS,
            actions: assign({
              definition: ({ event }) => event.definition,
              draftSeed: null,
            }),
          },
        ],
      },
    },
  },
});
